/**
 * /booked/:slug — post-booking thank-you page (v3).
 *
 * HubSpot no longer passes contact info in the redirect URL (the
 * "Add contact info to redirect URL" toggle was removed). Instead, we:
 *
 *   1. Show the branded confirmation page immediately (config loads fast).
 *   2. Extract hs_meeting_source_id from the redirect URL — HubSpot always
 *      appends this, even without the toggle.
 *   3. Poll GET /api/public/booked/confirm-status?meetingId=<id> every 3s.
 *   4. Meanwhile, the HubSpot Workflow fires its webhook → engine stores the
 *      meeting_bookings row with the contact's name + meeting time.
 *   5. When the poll finds the row (<60s), the heading area animates to the
 *      personalised confirmation: "You're confirmed, Sarah!"
 *   6. After 60s without a row, the page settles to a generic confirmation.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BRAND_HOME_URL, SUPPORT_EMAIL } from './branding';
import BrokenPage from './components/BrokenPage';
import { PolicyLinks } from './components/PolicyLinks';

const API = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

// ── types ──────────────────────────────────────────────────────────────────

interface PreCallQuestion {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  options?: string[];
  required?: boolean;
  placeholder?: string;
}

interface Stat { value: string; label: string; }

interface BookingConfig {
  slug: string;
  title: string;
  bookingThankyou: {
    heading?: string;
    subheading?: string;
    personSlug?: string;
    highlights?: string[];
    stats?: Stat[];
    quote?: string;
    /** Eyebrow above the person card. Defaults to call-framed copy — this is a
     *  booked-call page, not a webinar, so never "Your host". */
    personEyebrow?: string;
    blocks?: unknown[];
    preCAllForm?: {
      enabled: boolean;
      heading?: string;
      questions: PreCallQuestion[];
      submitLabel?: string;
      successMessage?: string;
    };
  };
  person: {
    slug: string;
    name: string;
    role: string | null;
    bio: string | null;
    firstName: string | null;
    photoUrl: string | null;
  } | null;
  branding: Record<string, unknown>;
  brand?: {
    logoUrl: string | null;
    logoLinkUrl: string | null;
    name: string;
  };
}

// Polling phases for the hero heading area.
type ConfirmPhase =
  | 'polling'   // have hs_meeting_source_id, waiting for webhook row
  | 'found'     // webhook row found, showing personalised name + time
  | 'settled';  // timed out or no meeting ID — show generic confirmation

// ── helpers ─────────────────────────────────────────────────────────────────

function parseStartTime(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!isNaN(n) && n > 1_000_000_000_000) return new Date(n);
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function formatMeetingTime(date: Date): string {
  try {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const dateStr  = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const timeStr  = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
    return `${weekday}, ${dateStr} at ${timeStr}`;
  } catch {
    return date.toLocaleString();
  }
}

const DEFAULT_HIGHLIGHTS = [
  "A clear read on your client pipeline — where the gaps are and exactly what's holding you back right now",
  "The one positioning shift most bookkeepers miss — it's usually just one sentence away from attracting right-fit clients",
  'A specific, honest action plan tailored to your business — not a generic template, but something you can move on this week',
];

const DEFAULT_STATS: Stat[] = [
  { value: '3,200+', label: "Bookkeepers have taken The Clients Blueprint" },
  { value: '600+',   label: "One-on-one coaching calls with Ron's team" },
  { value: '90 days', label: 'Typical time to see real, meaningful momentum' },
];

const POLL_INTERVAL_MS  = 3000;
const POLL_MAX_ATTEMPTS = 20; // 20 × 3s = 60s

// ── component ────────────────────────────────────────────────────────────────

export default function BookingConfirmedPage() {
  const { slug = '' } = useParams<{ slug: string }>();

  const searchParams = useRef(new URLSearchParams(window.location.search));
  // hs_meeting_source_id is appended by HubSpot's scheduling page on every redirect.
  const hsMeetingId = searchParams.current.get('hs_meeting_source_id') ?? null;
  // bid is appended by booking confirmation/reminder emails — it's the engine's
  // meeting_bookings id, letting the pre-call form submit without the HubSpot
  // redirect param (no polling needed; the booking already exists).
  const emailBookingId = searchParams.current.get('bid') ?? null;

  // Config load state
  const [status,     setStatus]     = useState<'loading' | 'ready' | 'error'>('loading');
  const [config,     setConfig]     = useState<BookingConfig | null>(null);
  const [bookingId,  setBookingId]  = useState<string | null>(emailBookingId);
  const [answers,    setAnswers]    = useState<Record<string, string>>({});
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');

  // Confirmation polling state
  const [confirmPhase,  setConfirmPhase]  = useState<ConfirmPhase>(hsMeetingId ? 'polling' : 'settled');
  const [confirmedName, setConfirmedName] = useState<string>('');
  const [confirmedAt,   setConfirmedAt]   = useState<string | null>(null);
  const [justConfirmed, setJustConfirmed] = useState(false); // triggers CSS entrance animation

  // ── Config load ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!slug) { setStatus('error'); return; }
    let cancelled = false;

    (async () => {
      try {
        const configRes = await fetch(`${API}/api/public/booked/${encodeURIComponent(slug)}`);
        if (!configRes.ok) { if (!cancelled) setStatus('error'); return; }
        const data = await configRes.json() as BookingConfig;
        if (cancelled) return;
        setConfig(data);
        setStatus('ready');

        // Inject brand font
        const fontUrl = data.branding?.fontImportUrl as string | undefined;
        if (fontUrl && !document.querySelector(`link[href="${fontUrl}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = fontUrl;
          document.head.appendChild(link);
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [slug]);

  // ── Register pending redirect ─────────────────────────────────────────────
  //
  // HubSpot's redirect fires before the Workflow webhook. Registering the
  // hs_meeting_source_id immediately lets the webhook handler back-fill
  // meeting_id + redirect_confirmed_at on the booking row it creates moments
  // later, so the poll below can find it.

  useEffect(() => {
    if (!hsMeetingId) return;
    fetch(`${API}/api/public/booked/register-redirect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ meetingSourceId: hsMeetingId, assessmentSlug: slug }),
    }).catch(() => { /* non-fatal */ });
  }, [hsMeetingId, slug]);
  // ── Confirmation polling ──────────────────────────────────────────────────
  //
  // Fires when hs_meeting_source_id is present in the redirect URL.
  // Polls every 3s until the HubSpot Workflow webhook has created the row,
  // then surfaces the contact's name + meeting time back to the page.

  useEffect(() => {
    if (!hsMeetingId) return; // settled already

    let attempt = 0;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const res = await fetch(
          `${API}/api/public/booked/confirm-status?meetingId=${encodeURIComponent(hsMeetingId)}&slug=${encodeURIComponent(slug)}`,
        );
        if (!stopped && res.ok) {
          const data = await res.json() as {
            found: boolean;
            bookingId?: string;
            firstName?: string | null;
            lastName?: string | null;
            scheduledAt?: string | null;
          };

          if (data.found) {
            stopped = true;
            if (data.bookingId) setBookingId(data.bookingId);
            setConfirmedName((data.firstName ?? '').trim());
            setConfirmedAt(data.scheduledAt ?? null);
            setConfirmPhase('found');
            setJustConfirmed(true);
            return;
          }
        }
      } catch { /* ignore — retry next tick */ }

      attempt++;
      if (!stopped) {
        if (attempt >= POLL_MAX_ATTEMPTS) {
          setConfirmPhase('settled');
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    poll();
    return () => { stopped = true; };
  }, [hsMeetingId, slug]);

  // ── Branding tokens ───────────────────────────────────────────────────────

  const b       = config?.branding ?? {};
  const primary = (b.primaryColor    as string) ?? '#FF6B35';
  const accent  = (b.accentColor     as string) ?? '#0A0A2E';
  const bg      = (b.backgroundColor as string) ?? '#F8F6F3';
  const font    = (b.fontFamily      as string) ?? "'Poppins', sans-serif";
  const brand   = config?.brand ?? null;

  // ── Content ───────────────────────────────────────────────────────────────

  const th         = config?.bookingThankyou ?? {};
  const person     = config?.person ?? null;
  const form       = (th as any).preCAllForm as BookingConfig['bookingThankyou']['preCAllForm'];
  const highlights = ((th as any).highlights as string[] | undefined) ?? DEFAULT_HIGHLIGHTS;
  const stats      = ((th as any).stats      as Stat[]   | undefined) ?? DEFAULT_STATS;
  const quote      = ((th as any).quote      as string   | undefined) ?? null;

  // Prefer confirmed name (from webhook) over anything in URL (usually empty).
  const displayName = confirmPhase === 'found' ? confirmedName : '';
  const meetingDate = parseStartTime(confirmPhase === 'found' ? confirmedAt : null);

  const heading = th.heading
    ? (th.heading.includes('{name}')
        ? (displayName ? th.heading.replace('{name}', displayName) : 'Your call is confirmed!')
        : th.heading)
    : (displayName ? `You're confirmed, ${displayName}!` : 'Your call is confirmed!');

  // ── Pre-call form submit ───────────────────────────────────────────────────

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookingId || formStatus === 'submitting' || formStatus === 'done') return;
    setFormStatus('submitting');
    try {
      const res = await fetch(`${API}/api/public/booked/${encodeURIComponent(bookingId)}/answers`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      setFormStatus(res.ok ? 'done' : 'error');
    } catch {
      setFormStatus('error');
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F6F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: '3px solid #FF6B35', borderTopColor: 'transparent',
          animation: 'spin 0.75s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────

  if (status === 'error') {
    return <BrokenPage variant="error" />;
  }

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: bg, fontFamily: font, display: 'flex', flexDirection: 'column' }}>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .booking-confirmed-enter {
          animation: fadeInUp 0.4s ease forwards;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.45; }
        }
        .booking-processing-pulse {
          animation: pulse 1.6s ease-in-out infinite;
        }
      `}</style>

      <TopBar brand={brand} />

      <main style={{ flex: 1, maxWidth: 780, margin: '0 auto', padding: '48px 20px 80px', width: '100%', boxSizing: 'border-box' }}>

        {/* ── Hero confirmation card ── */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 'clamp(32px, 5vw, 52px) clamp(24px, 5vw, 52px)',
          textAlign: 'center',
          marginBottom: 24,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 16px 48px rgba(0,0,0,0.08)',
        }}>

          {confirmPhase === 'polling' ? (
            /* ── Processing state ── */
            <div>
              {/* Spinner ring */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <div style={{
                  width: 68, height: 68, borderRadius: '50%',
                  background: '#f0fdf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%',
                    border: `3px solid ${primary}`,
                    borderTopColor: 'transparent',
                    animation: 'spin 0.75s linear infinite',
                  }} />
                </div>
              </div>

              <h1 style={{ margin: '0 0 10px', fontSize: 'clamp(20px, 3.5vw, 26px)', fontWeight: 700, color: accent, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                Thank you — your booking is on its way.
              </h1>

              <p style={{ margin: '0 0 20px', fontSize: 15, color: '#64748B', lineHeight: 1.6, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
                We're confirming your call now. Take a moment to read what to expect below — this usually takes less than 30 seconds.
              </p>

              {/* Progress dots */}
              <div className="booking-processing-pulse" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: primary,
                    opacity: 0.6 + i * 0.15,
                    animationDelay: `${i * 0.2}s`,
                  }} />
                ))}
              </div>
            </div>
          ) : (
            /* ── Confirmed state (found or settled) ── */
            <div className={justConfirmed ? 'booking-confirmed-enter' : undefined}>
              {/* Check circle */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <div style={{
                  width: 68, height: 68, borderRadius: '50%',
                  background: '#f0fdf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden>
                    <circle cx="17" cy="17" r="16" fill="#22c55e" opacity={0.15} />
                    <polyline
                      points="9,17 14,22 25,11"
                      stroke="#16a34a"
                      strokeWidth="2.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </div>
              </div>

              <h1 style={{ margin: '0 0 10px', fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 700, color: accent, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                {heading}
              </h1>

              <p style={{ margin: '0 0 28px', fontSize: 16, color: '#475569', lineHeight: 1.6, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                {th.subheading ??
                  'Get ready — this conversation is the starting point for more clients, more revenue, and a bookkeeping business that finally works on your terms.'}
              </p>

              {/* Meeting time chip */}
              {meetingDate ? (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  background: `${primary}12`, border: `1.5px solid ${primary}35`,
                  borderRadius: 12, padding: '13px 22px',
                }}>
                  <CalendarIcon color={primary} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: accent }}>{formatMeetingTime(meetingDate)}</span>
                </div>
              ) : (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  background: `${primary}12`, border: `1.5px solid ${primary}35`,
                  borderRadius: 12, padding: '13px 22px',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#64748B' }}>Check your inbox for your calendar invite</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Pre-call form ── */}
        {form?.enabled && confirmPhase !== 'polling' && (
          <div style={{
            background: '#EFFAF3',
            border: '1px solid rgba(34,197,94,0.28)',
            borderRadius: 20,
            padding: 'clamp(24px, 4vw, 36px)',
            marginBottom: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.06)',
          }}>
            {formStatus === 'done' ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 14 }}>
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden style={{ display: 'block', margin: '0 auto' }}>
                    <circle cx="24" cy="24" r="22" fill="#22c55e" opacity={0.12} />
                    <polyline points="13,24 20,31 35,16" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </div>
                <p style={{ fontSize: 16, color: '#16A34A', fontWeight: 700, margin: '0 0 6px' }}>
                  {form.successMessage ?? 'Got it — Ron will review your answers before the call.'}
                </p>
                <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>You're all set. See you on the call.</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} noValidate>
                <h2 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: accent, letterSpacing: '-0.02em' }}>
                  {form.heading ?? `Help ${person?.firstName ?? 'us'} prepare for your call`}
                </h2>
                <p style={{ margin: '0 0 24px', fontSize: 13.5, color: '#64748B', lineHeight: 1.5 }}>
                  Takes 2 minutes — makes the conversation twice as useful.
                </p>

                {form.questions.map((q) => (
                  <div key={q.id} style={{ marginBottom: 18 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }} htmlFor={`pcf-${q.id}`}>
                      {q.label}
                      {q.required && <span style={{ color: '#EF4444', marginLeft: 3 }}>*</span>}
                    </label>
                    {q.type === 'textarea' ? (
                      <textarea
                        id={`pcf-${q.id}`}
                        style={inputBaseStyle}
                        value={answers[q.id] ?? ''}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                        placeholder={q.placeholder}
                        required={q.required}
                        rows={4}
                      />
                    ) : q.type === 'select' ? (
                      <select
                        id={`pcf-${q.id}`}
                        style={inputBaseStyle}
                        value={answers[q.id] ?? ''}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                        required={q.required}
                      >
                        <option value="">Select one…</option>
                        {(q.options ?? []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : (
                      <input
                        id={`pcf-${q.id}`}
                        type="text"
                        style={inputBaseStyle}
                        value={answers[q.id] ?? ''}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                        placeholder={q.placeholder}
                        required={q.required}
                      />
                    )}
                  </div>
                ))}

                {formStatus === 'error' && (
                  <p style={{ fontSize: 13, color: '#EF4444', margin: '0 0 12px 0' }}>
                    Something went wrong — please try again.
                  </p>
                )}

                {bookingId ? (
                  <button
                    type="submit"
                    style={{
                      display: 'block', width: '100%',
                      padding: '14px 20px',
                      background: primary, color: '#fff',
                      border: 'none', borderRadius: 10,
                      fontSize: 15, fontWeight: 700,
                      cursor: formStatus === 'submitting' ? 'wait' : 'pointer',
                      fontFamily: 'inherit', letterSpacing: '-0.01em',
                    }}
                    disabled={formStatus === 'submitting'}
                  >
                    {formStatus === 'submitting'
                      ? 'Saving…'
                      : (form.submitLabel ?? `Send to ${person?.firstName ?? 'the team'} →`)}
                  </button>
                ) : (
                  <p style={{ fontSize: 13, color: '#94A3B8', margin: '8px 0 0' }}>
                    Linking your booking — submit will appear shortly.
                  </p>
                )}
              </form>
            )}
          </div>
        )}

        {/* ── What you'll get from this call ── */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ margin: '0 0 12px', fontSize: 11.5, fontWeight: 700, color: primary, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            On your call, you'll get
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {highlights.map((text, i) => (
              <div
                key={i}
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  padding: '16px 20px',
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)',
                }}
              >
                <div style={{
                  width: 28, height: 28,
                  borderRadius: '50%',
                  background: primary,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: 1,
                }}>
                  {i + 1}
                </div>
                <p style={{ margin: 0, fontSize: 14.5, color: '#1e293b', lineHeight: 1.55, fontWeight: 500 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Stats row ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 24,
        }}>
          {stats.map((s, i) => (
            <div
              key={i}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: '22px 16px',
                textAlign: 'center',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{ fontSize: 28, fontWeight: 800, color: primary, letterSpacing: '-0.04em', lineHeight: 1, marginBottom: 6 }}>
                {s.value}
              </div>
              <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.45, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Who you'll be talking with ── */}
        {person && (
          <div style={{
            background: accent,
            borderRadius: 20,
            padding: 'clamp(24px, 4vw, 36px) clamp(24px, 4vw, 40px)',
            marginBottom: 24,
            display: 'flex',
            gap: 28,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              {person.photoUrl && (
                <img
                  src={person.photoUrl}
                  alt={person.name}
                  style={{
                    width: 100, height: 100,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `3px solid ${primary}`,
                    display: 'block',
                  }}
                />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <p style={{ margin: '0 0 3px', fontSize: 11, fontWeight: 700, color: primary, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {th.personEyebrow ?? "You'll be talking with"}
              </p>
              <h3 style={{ margin: '0 0 3px', fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
                {person.name}
              </h3>
              {person.role && (
                <p style={{ margin: '0 0 16px', fontSize: 13, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                  {/Successful Bookkeeper/i.test(person.role) ? person.role : `${person.role}, The Successful Bookkeeper`}
                </p>
              )}
              {quote && (
                <div style={{
                  margin: '0 0 14px',
                  padding: '14px 18px',
                  background: 'rgba(255,255,255,0.07)',
                  borderLeft: `3px solid ${primary}`,
                  borderRadius: '0 10px 10px 0',
                }}>
                  <p style={{ margin: 0, fontSize: 14.5, color: 'rgba(255,255,255,0.92)', fontStyle: 'italic', lineHeight: 1.65 }}>
                    "{quote}"
                  </p>
                </div>
              )}
              {person.bio && (
                <p style={{ margin: 0, fontSize: 13.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65 }}>
                  {person.bio}
                </p>
              )}
            </div>
          </div>
        )}

      </main>

      <footer style={{ padding: '20px 24px', textAlign: 'center', fontSize: 12, color: '#94A3B8', borderTop: '1px solid #E8EDF2' }}>
        <a href={BRAND_HOME_URL} style={{ color: '#94A3B8', textDecoration: 'none' }}>The Successful Bookkeeper</a>
        {' · '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#94A3B8', textDecoration: 'none' }}>{SUPPORT_EMAIL}</a>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>

    </div>
  );
}

// ── sub-components ───────────────────────────────────────────────────────────

function TopBar({ brand }: { brand: BookingConfig['brand'] | null }) {
  const logoUrl = brand?.logoUrl ?? null;
  const linkUrl = brand?.logoLinkUrl ?? BRAND_HOME_URL;
  const name = brand?.name ?? 'The Successful Bookkeeper';
  return (
    <header style={{
      background: '#fff',
      padding: '14px 24px',
      flexShrink: 0,
      borderBottom: '1px solid #E8EDF2',
    }}>
      <div style={{ maxWidth: 780, margin: '0 auto' }}>
        <a href={linkUrl} style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} aria-label={name}>
          {logoUrl
            ? <img src={logoUrl} alt={name} style={{ height: 36, display: 'block' }} />
            : <span style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em' }}>{name}</span>}
        </a>
      </div>
    </header>
  );
}

function CalendarIcon({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="1" y="2.5" width="14" height="12" rx="2" stroke={color} strokeWidth="1.5" />
      <path d="M1 6.5h14" stroke={color} strokeWidth="1.5" />
      <path d="M5 1v3M11 1v3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// ── shared input style ───────────────────────────────────────────────────────

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  border: '1.5px solid #E2E8F0',
  borderRadius: 8,
  padding: '10px 13px',
  fontSize: 14,
  color: '#0F172A',
  background: '#fff',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  outline: 'none',
  resize: 'vertical' as const,
  lineHeight: 1.5,
};
